import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { escapeHtml } from '../utils/catalog-labels';
import { isAdmin, renderConfirmModal, bindModal, showModal } from '../utils/ui';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface MachineRef {
  id: string;
  name: string;
  code: string;
  status: string;
}

interface Exercise {
  id: string;
  name: string;
  description?: string | null;
  machineId?: string | null;
  machine?: MachineRef | null;
  gifPath?: string | null;
  restSec: number;
  createdAt: string;
}

type MachineFilter = 'all' | 'with_machine' | 'without_machine';
type FormMode = 'none' | 'create' | 'edit';

export class AdminExercisesPage implements View {
  private exercises: Exercise[] = [];
  private machines: MachineRef[] = [];
  private loading = true;
  private errorMsg: string | null = null;
  private search = '';
  private machineFilter: MachineFilter = 'all';
  private formMode: FormMode = 'none';
  private editingId: string | null = null;
  private formName = '';
  private formDescription = '';
  private formMachineId = '';
  private formGifUrl = '';
  private deleteExerciseId: string | null = null;

  async render(): Promise<string> {
    this.readParams();
    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-exercises-loading"><p>Cargando catálogo de ejercicios...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-exercises-error">
          <h3>Error al cargar ejercicios</h3>
          <p>${escapeHtml(this.errorMsg)}</p>
          <button class="btn btn-primary" onclick="window.location.reload()">Reintentar</button>
        </div>
      `;
    }

    const filtered = this.getFiltered();
    const editing = this.formMode === 'edit' ? this.exercises.find((e) => e.id === this.editingId) : null;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <nav class="admin-breadcrumb">
            <span>Catálogo de ejercicios</span>
          </nav>
          <h2 data-testid="admin-exercises-title">Catálogo de ejercicios</h2>
          <p>Base del inventario para rutinas template y rutinas asignadas.</p>
        </div>
        <button type="button" class="btn btn-primary" id="btn-new-exercise" data-testid="btn-new-exercise">
          ${getTyrisIcon('plus', { size: 16 })} Nuevo ejercicio
        </button>
      </div>

      ${this.formMode !== 'none' ? this.renderForm(editing) : ''}

      <div class="admin-filters card-glass" data-testid="admin-exercises-filters">
        <div class="admin-filter-row">
          <label class="admin-filter-label" for="exercise-search">Buscar</label>
          <input id="exercise-search" type="search" class="admin-input" placeholder="Nombre del ejercicio..."
            value="${escapeHtml(this.search)}" data-testid="exercise-search-input" />
        </div>
        <div class="admin-filter-row">
          <label class="admin-filter-label" for="exercise-machine-filter">Máquina</label>
          <select id="exercise-machine-filter" class="admin-select" data-testid="exercise-machine-filter">
            <option value="all" ${this.machineFilter === 'all' ? 'selected' : ''}>Todos</option>
            <option value="with_machine" ${this.machineFilter === 'with_machine' ? 'selected' : ''}>Con máquina</option>
            <option value="without_machine" ${this.machineFilter === 'without_machine' ? 'selected' : ''}>Sin máquina</option>
          </select>
        </div>
      </div>

      <div class="card-glass admin-table-wrap" data-testid="admin-exercises-table">
        ${
          filtered.length === 0
            ? `<div class="admin-empty" data-testid="admin-exercises-empty"><p>No hay ejercicios que coincidan con los filtros.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Máquina</th>
                <th>Descanso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map((ex) => this.renderRow(ex)).join('')}
            </tbody>
          </table>
        `
        }
      </div>

      ${renderConfirmModal({
        id: 'confirm-delete-exercise',
        title: '¿Eliminar ejercicio?',
        body: 'Esta acción no se puede deshacer y puede afectar a las rutinas asignadas y templates.',
        confirmLabel: 'Eliminar',
        danger: true,
      })}
    `;
  }

  afterRender(): void {
    $('#btn-new-exercise')?.addEventListener('click', () => {
      window.location.hash = '#/admin/exercises?form=create';
    });

    $('#btn-cancel-exercise-form')?.addEventListener('click', () => {
      window.location.hash = '#/admin/exercises';
    });

    const searchInput = document.getElementById('exercise-search') as HTMLInputElement | null;
    const machineSelect = document.getElementById('exercise-machine-filter') as HTMLSelectElement | null;

    const applyFilters = () => {
      const parts = new URLSearchParams();
      const q = searchInput?.value.trim();
      const mf = machineSelect?.value ?? 'all';
      if (q) parts.set('q', q);
      if (mf !== 'all') parts.set('machine', mf);
      if (this.formMode !== 'none') {
        parts.set('form', this.formMode);
        if (this.editingId) parts.set('id', this.editingId);
      }
      const query = parts.toString();
      window.location.hash = query ? `#/admin/exercises?${query}` : '#/admin/exercises';
    };

    let debounce: ReturnType<typeof setTimeout> | undefined;
    searchInput?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(applyFilters, 300);
    });
    machineSelect?.addEventListener('change', applyFilters);

    $('#form-exercise')?.addEventListener('submit', (e) => this.handleSubmit(e));

    document.querySelectorAll('[data-exercise-edit]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = (el as HTMLElement).getAttribute('data-exercise-edit');
        if (id) window.location.hash = `#/admin/exercises?form=edit&id=${id}`;
      });
    });

    document.querySelectorAll('[data-exercise-delete]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = (el as HTMLElement).getAttribute('data-exercise-delete');
        if (id) {
          this.deleteExerciseId = id;
          showModal('confirm-delete-exercise');
        }
      });
    });

    bindModal({
      modalId: 'confirm-delete-exercise',
      onConfirm: async () => {
        if (this.deleteExerciseId) {
          await this.executeDelete(this.deleteExerciseId);
        }
      },
    });
  }

  private renderRow(ex: Exercise): string {
    const desc = ex.description?.trim() || '—';
    const machineLabel = ex.machine
      ? `${ex.machine.name} (${ex.machine.code})`
      : '—';
    const truncated =
      desc.length > 60 ? `${escapeHtml(desc.slice(0, 60))}…` : escapeHtml(desc);

    const deleteBtn = isAdmin()
      ? `<button type="button" class="btn btn-outline btn-sm" data-exercise-delete="${ex.id}" data-testid="exercise-delete-${ex.id}"
            style="border-color:rgba(220,53,69,0.35);color:hsl(350,80%,65%);">${getTyrisIcon('darse-de-baja', { size: 14 })}</button>`
      : '';

    const gifBadge = ex.gifPath
      ? `<span class="badge" style="background-color:rgba(34,197,94,0.15);color:#22c55e;border:1px solid rgba(34,197,94,0.3);font-size:0.7rem;padding:0.1rem 0.35rem;margin-left:0.5rem;border-radius:3px;font-weight:600;display:inline-block;vertical-align:middle;">GIF</span>`
      : '';

    return `
      <tr data-testid="exercise-row-${ex.id}">
        <td><span class="admin-cell-primary">${escapeHtml(ex.name)}${gifBadge}</span></td>
        <td class="admin-cell-muted">${truncated}</td>
        <td>${escapeHtml(machineLabel)}</td>
        <td data-testid="exercise-rest-${ex.id}">${ex.restSec} s</td>
        <td class="admin-row-actions">
          <button type="button" class="btn btn-outline btn-sm" data-exercise-edit="${ex.id}" data-testid="exercise-edit-${ex.id}">${getTyrisIcon('editar', { size: 14 })}</button>
          ${deleteBtn}
        </td>
      </tr>
    `;
  }

  private renderForm(editing: Exercise | null | undefined): string {
    const title = this.formMode === 'create' ? 'Nuevo ejercicio' : 'Editar ejercicio';
    const name = this.formName || editing?.name || '';
    const description = this.formDescription || editing?.description || '';
    const machineId = this.formMachineId || editing?.machineId || '';
    const gifUrl = this.formGifUrl || editing?.gifPath || '';

    return `
      <div class="card-glass admin-form-card" data-testid="admin-exercise-form">
        <h3 class="admin-section-title">${title}</h3>
        <form id="form-exercise" novalidate>
          <div class="admin-form-grid">
            <label>Nombre *
              <input name="name" class="admin-input" required minlength="2" maxlength="120"
                value="${escapeHtml(name)}" data-testid="exercise-form-name" />
            </label>
            <label>Máquina (opcional)
              <select name="machineId" class="admin-select" data-testid="exercise-form-machine">
                <option value="">— Sin máquina —</option>
                ${this.machines
                  .map(
                    (m) => `
                  <option value="${m.id}" ${machineId === m.id ? 'selected' : ''}>
                    ${escapeHtml(m.name)} (${escapeHtml(m.code)})
                  </option>
                `,
                  )
                  .join('')}
              </select>
            </label>
            <label>Descanso recomendado (s) *
              <input name="restSec" type="number" class="admin-input" min="0" max="600" step="5"
                value="${editing?.restSec ?? 120}" required data-testid="exercise-form-rest" />
            </label>
          </div>
          <label class="admin-form-block">Descripción
            <textarea name="description" class="admin-input admin-textarea" rows="3"
              data-testid="exercise-form-description">${escapeHtml(description)}</textarea>
          </label>
          <label class="admin-form-block">
            <span class="admin-form-hint">
              URL de imagen / GIF de técnica
              <small>(opcional — se mostrará en "Ver Técnica" durante el entrenamiento)</small>
            </span>
            <input name="gifUrl" class="admin-input" type="url"
              placeholder="https://ejemplo.com/imagen.gif"
              value="${escapeHtml(gifUrl)}"
              data-testid="exercise-form-gif-url" />
            ${gifUrl ? `
              <span class="admin-form-preview">
                <img src="${escapeHtml(gifUrl)}" alt="Vista previa de técnica" />
                Vista previa actual
              </span>` : ''}
          </label>
          <div class="admin-form-actions">
            <button type="submit" class="btn btn-primary" data-testid="exercise-form-submit">Guardar</button>
            <button type="button" class="btn btn-outline" id="btn-cancel-exercise-form">Cancelar</button>
          </div>
        </form>
      </div>
    `;
  }

  private readParams(): void {
    const params = router.getParams();
    this.search = (params.q ?? '').toLowerCase();
    if (params.machine && ['all', 'with_machine', 'without_machine'].includes(params.machine)) {
      this.machineFilter = params.machine as MachineFilter;
    }
    if (params.form === 'create') {
      this.formMode = 'create';
      this.editingId = null;
    } else if (params.form === 'edit' && params.id) {
      this.formMode = 'edit';
      this.editingId = params.id;
    } else {
      this.formMode = 'none';
      this.editingId = null;
    }
  }

  private getFiltered(): Exercise[] {
    return this.exercises.filter((ex) => {
      if (this.search && !ex.name.toLowerCase().includes(this.search)) return false;
      if (this.machineFilter === 'with_machine' && !ex.machineId) return false;
      if (this.machineFilter === 'without_machine' && ex.machineId) return false;
      return true;
    });
  }

  private async loadData(): Promise<void> {
    try {
      const [exercises, machines] = await Promise.all([
        apiClient.get<Exercise[]>('/api/exercises'),
        apiClient.get<MachineRef[]>('/api/machines'),
      ]);
      this.exercises = exercises;
      this.machines = machines;
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
    } finally {
      this.loading = false;
    }
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const name = String(fd.get('name') ?? '').trim();
    const description = String(fd.get('description') ?? '').trim();
    const machineId = String(fd.get('machineId') ?? '').trim();
    const gifUrl = String(fd.get('gifUrl') ?? '').trim();
    const restSecRaw = String(fd.get('restSec') ?? '').trim();
    const restSec = restSecRaw === '' ? 120 : Number(restSecRaw);

    if (name.length < 2) {
      toast.error('El nombre del ejercicio es obligatorio (mínimo 2 caracteres).');
      return;
    }

    if (!Number.isInteger(restSec) || restSec < 0 || restSec > 600) {
      toast.error('El descanso debe ser un entero entre 0 y 600 segundos.');
      return;
    }

    if (gifUrl && /^[a-z][a-z0-9+.-]*:\/\//i.test(gifUrl)) {
      try {
        new URL(gifUrl);
      } catch {
        toast.error('La URL de imagen/GIF no es válida. Asegurate de incluir https://');
        return;
      }
    }

    const payload: {
      name: string;
      description?: string;
      machineId?: string;
      gifPath?: string;
      restSec: number;
    } = { name, restSec };
    if (description) payload.description = description;
    if (machineId) payload.machineId = machineId;
    if (gifUrl) payload.gifPath = gifUrl;

    try {
      if (this.formMode === 'edit' && this.editingId) {
        await apiClient.patch(`/api/exercises/${this.editingId}`, payload);
        toast.success('Ejercicio actualizado.');
      } else {
        await apiClient.post('/api/exercises', payload);
        toast.success('Ejercicio creado.');
      }
      this.formGifUrl = '';
      window.location.hash = '#/admin/exercises';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      toast.error(msg ?? 'No se pudo guardar el ejercicio.');
    }
  }

  private async executeDelete(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/exercises/${id}`);
      toast.success('Ejercicio eliminado.');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo eliminar. Puede estar en uso en rutinas.');
    } finally {
      this.deleteExerciseId = null;
    }
  }
}
